import * as yup from 'yup';

import { CSV_BULK_ADDRESSES_UPLOAD_EXAMPLE_URL } from '../config/app';
import { IOrgAddress, TNotificationAlert } from '../types/addressBook';
import { getCountLabel } from '../utils/helpers';
import { CANADA, countryList, USA } from './countries';
import { NO_DEPARTMENT } from './organizations';
import { EMAIL_VALIDATION_REGEX, ZIP_VALIDATION_REGEX } from './shell';
import { caProvincesList, usStatesList } from './states';

export const UID = 'uid';
export const LABEL = 'label';
export const ADDRESS1 = 'address1';
export const ADDRESS2 = 'address2';
export const CITY = 'city';
export const STATE = 'state';
export const COUNTRY = 'country';
export const ZIP = 'zip';
export const EMAIL = 'email';
export const PHONE = 'phone';
export const STATUS = 'status';
export const DEPARTMENT_IDS = 'department_ids';
export const NEW_ID = 'new';
export const COMPANY_NAME = 'company_name';
export const SUCCESS = 'success';
export const ACTIONS = 'actions';

export enum OrgAddressFilterTypesEnum {
  All = 'all',
  Active = 'active',
  NotActive = 'not_active',
}

export enum ShippingFlowSelectorOptionsEnum {
  RecipientDecides = 'recipient_decides',
  RecipientDecidesFromList = 'recipient_decides_from_list',
  Fixed = 'fixed',
}

export const ADDRESS_FILTERS = [
  {
    label: 'All',
    filterId: OrgAddressFilterTypesEnum.All,
  },
  {
    label: 'Active',
    filterId: OrgAddressFilterTypesEnum.Active,
  },
  {
    label: 'Inactive',
    filterId: OrgAddressFilterTypesEnum.NotActive,
  },
];

export const ORG_ADDRESS_INITIAL_VALUES: Partial<IOrgAddress> = {
  [LABEL]: '',
  [COMPANY_NAME]: '',
  [ADDRESS1]: '',
  [ADDRESS2]: '',
  [CITY]: '',
  [STATE]: '',
  [COUNTRY]: '',
  [ZIP]: '',
  [EMAIL]: '',
  [PHONE]: '',
  [DEPARTMENT_IDS]: [NO_DEPARTMENT],
};

export const ORG_ADDRESS_FORM_FIELDS_MAP: { [k: string]: string } = {
  [LABEL]: 'Label',
  [COMPANY_NAME]: 'Company name',
  [ADDRESS1]: 'Address 1',
  [CITY]: 'City',
  [STATE]: 'State',
  [COUNTRY]: 'Country',
  [ZIP]: 'Zip',
  [DEPARTMENT_IDS]: 'Department',
};

export const ORG_ADDRESS_VALIDATION_SCHEMA = yup.object().shape({
  [LABEL]: yup
    .string()
    .transform((value) => value?.trim())
    .label(ORG_ADDRESS_FORM_FIELDS_MAP[LABEL])
    .required()
    .max(70, 'Label is too long'),
  [COMPANY_NAME]: yup.string().transform((value) => value?.trim()),
  [ADDRESS1]: yup
    .string()
    .transform((value) => value?.trim())
    .label(ORG_ADDRESS_FORM_FIELDS_MAP[ADDRESS1])
    .required(),
  [ADDRESS2]: yup.string().transform((value) => value?.trim()),
  [CITY]: yup
    .string()
    .transform((value) => value?.trim())
    .label(ORG_ADDRESS_FORM_FIELDS_MAP[CITY])
    .required(),
  [STATE]: yup
    .string()
    .transform((value) => value?.trim())
    .when(COUNTRY, {
      is: (country: string) => country === USA.two_digit_code || country === CANADA.two_digit_code,
      then: (schema: yup.StringSchema) => schema.required().label(ORG_ADDRESS_FORM_FIELDS_MAP[STATE]),
      otherwise: (schema: yup.StringSchema) => schema.notRequired(),
    }),
  [COUNTRY]: yup
    .string()
    .transform((value) => value?.trim())
    .label(ORG_ADDRESS_FORM_FIELDS_MAP[COUNTRY])
    .required(),
  [ZIP]: yup
    .string()
    .transform((value) => value?.trim())
    .label(ORG_ADDRESS_FORM_FIELDS_MAP[ZIP])
    .matches(ZIP_VALIDATION_REGEX, { message: 'Invalid zip format' })
    .required(),
  [EMAIL]: yup
    .string()
    .transform((value) => value?.trim())
    .notRequired()
    .matches(EMAIL_VALIDATION_REGEX, { message: 'Invalid email format', excludeEmptyString: true }),
  [PHONE]: yup.string().transform((value) => value?.trim()),
  [DEPARTMENT_IDS]: yup.array().of(yup.string()).label(ORG_ADDRESS_FORM_FIELDS_MAP[DEPARTMENT_IDS]).required(),
});

const validCountryCodes = new Set(countryList.map((country) => country.two_digit_code));
const validUSStateCodes = new Set(usStatesList.map((state) => state.two_digit_code));
const validCAProvinceCodes = new Set(caProvincesList.map((province) => province.two_digit_code));

export const ORG_ADDRESS_BULK_CSV_VALIDATION_SCHEMA = ORG_ADDRESS_VALIDATION_SCHEMA.shape({
  [COUNTRY]: yup
    .string()
    .label(ORG_ADDRESS_FORM_FIELDS_MAP[COUNTRY])
    .required()
    .test(
      'is-valid-country-code',
      'Invalid country code. Must be a valid 2-letter code (e.g., US, NE, CA).',
      (value) => {
        if (!value) {
          return false;
        }
        return /^[A-Z]{2}$/.test(value) && validCountryCodes.has(value);
      },
    )
    .transform((value: string) => value?.trim()),
  [STATE]: yup
    .string()
    .transform((value: string) => value?.trim())
    .when(COUNTRY, {
      is: (country: string) => country === USA.two_digit_code || country === CANADA.two_digit_code,
      then: (schema: yup.StringSchema) =>
        schema
          .required()
          .label(ORG_ADDRESS_FORM_FIELDS_MAP[STATE])
          .test('is-valid-state-code', 'Invalid state/province code. Must be a valid 2-letter code.', function (value) {
            if (!value) {
              return false;
            }
            // tslint:disable-next-line:no-invalid-this
            const country = this.parent[COUNTRY];
            const validCodes = country === CANADA.two_digit_code ? validCAProvinceCodes : validUSStateCodes;
            return /^[A-Z]{2}$/.test(value) && validCodes.has(value);
          }),
      otherwise: (schema: yup.StringSchema) => schema.notRequired(),
    }),
});

export const ORG_ADDRESS_FORM_REQUIRED_FIELDS = [] as string[];

export const ORG_ADDRESS_FORM_REQUIRED_FIELDS_WITH_STATE = ORG_ADDRESS_FORM_REQUIRED_FIELDS.concat(STATE);

// Address Book CSV modal constants

export const UploadCsvModalSteps = [
  { num: 1, title: 'Upload addresses', label: 'Upload' },
  { num: 2, title: 'Map header columns', label: 'Map' },
  { num: 3, title: 'Check column values', label: 'Repair' },
  { num: 4, title: 'Check column values', label: 'Import' },
];

export enum UploadStepsEnum {
  Upload = 1,
  Map = 2,
  Repair = 3,
  Import = 4,
}

export const INITIAL_STEP = 1;

export const FILE_CSV_SIZE_MB_LIMIT = 3;
export const FILE_CSV_SIZE_BYTE_LIMIT = FILE_CSV_SIZE_MB_LIMIT * 1024 * 1024;

export const DROPZONE_TEXT = `CSV only · max ${FILE_CSV_SIZE_MB_LIMIT} MB · 1 file`;

export const DEFAULT_CSV_FILE_HEADERS = [
  'Label',
  'Company name',
  'Address 1',
  'Address 2',
  'City',
  'State',
  'Zip code',
  'Country',
  'Phone',
  'Email',
];

export const REQUIRED_AMOUNT_OF_COLUMNS = DEFAULT_CSV_FILE_HEADERS.length;

export const REQUIRED_CSV_FILE_HEADERS_STRING = DEFAULT_CSV_FILE_HEADERS.join(', ');

export enum DropZoneErrorTypeEnum {
  UnsupportedFile = 'unsupported_file',
  InvalidHeaders = 'invalid_headers',
  NoRowsWithData = 'no_rows_with_data',
}

export const DropZoneErrorText = {
  [DropZoneErrorTypeEnum.UnsupportedFile]: {
    title: 'Unsupported file.',
  },
  [DropZoneErrorTypeEnum.InvalidHeaders]: {
    title: 'Add column headers in Row 1, then upload again',
  },
  [DropZoneErrorTypeEnum.NoRowsWithData]: {
    title: 'Add at least one address below the header, then upload again',
  },
};

export const VISIBLE_ADDRESS_COLS_INITIAL_STATE = {
  [LABEL]: true,
  [COMPANY_NAME]: true,
  [ADDRESS1]: true,
  [ADDRESS2]: true,
  [CITY]: true,
  [STATE]: true,
  [ZIP]: true,
  [COUNTRY]: true,
  [STATUS]: true,
  [ACTIONS]: true,
};

export const WIDTH_MODAL_COMPONENTS = 600;

export enum UploadCsvStatusEnum {
  Idle = 'idle',
  Loading = 'loading',
  Complete = 'complete',
  Error = 'error',
  ErrorInvalidHeaders = 'error_invalid_headers',
  ErrorNoRowsWithData = 'error_no_rows_with_data',
  ErrorLargeFile = 'error_large_file',
}

export enum ColumnTypeEnum {
  Text = 'text',
  Address = 'address',
  City = 'city',
  State = 'state',
  ZipCode = 'zip_code',
  Country = 'country',
  Phone = 'phone',
  Email = 'email',
}

export const EXPECTED_COLUMNS = [
  { title: 'Label', sampleData: 'Head office', type: ColumnTypeEnum.Text, id: LABEL },
  { title: 'Company name', sampleData: 'Example Company', type: ColumnTypeEnum.Text, id: COMPANY_NAME },
  { title: 'Address 1', sampleData: '123 Example St', type: ColumnTypeEnum.Address, id: ADDRESS1 },
  { title: 'Address 2', sampleData: 'Suite 200 (optional)', type: ColumnTypeEnum.Address, id: ADDRESS2 },
  { title: 'City', sampleData: 'Sample City', type: ColumnTypeEnum.City, id: CITY },
  { title: 'State', sampleData: 'CA', type: ColumnTypeEnum.State, id: STATE },
  { title: 'Zip code', sampleData: '94016', type: ColumnTypeEnum.ZipCode, id: ZIP },
  { title: 'Country', sampleData: 'US', type: ColumnTypeEnum.Country, id: COUNTRY },
  { title: 'Phone', sampleData: '+12025550123', type: ColumnTypeEnum.Phone, id: PHONE },
  { title: 'Email', sampleData: 'user@example.com', type: ColumnTypeEnum.Email, id: EMAIL },
];

export const COLUMN_WIDTHS: Record<string, number> = {
  status: 36,
  label: 200,
  company_name: 200,
  address1: 200,
  address2: 200,
  city: 160,
  zip: 90,
  country: 80,
  state: 80,
  phone: 200,
  email: 200,
  actions: 39,
};

export const PREPARING_CSV_ALERT = {
  type: 'info' as TNotificationAlert,
  title: 'Preparing a CSV',
  headersDescription: 'Row 1 must be column headers:',
  requiredHeaders: REQUIRED_CSV_FILE_HEADERS_STRING,
  requiredRowsDescriptionBold: 'Include at least one data row;',
  requiredRowsDescription: 'header-only files fail',
  requiredHeadersHelper: 'Header row example (Row 1)',
  link: CSV_BULK_ADDRESSES_UPLOAD_EXAMPLE_URL,
  linkText: 'Download CSV template',
};

export const CONFIRMATION_MODAL = {
  title: 'Unsaved changes',
  description: 'If you leave now, your progress on this upload will be lost',
};

export const MAP_COLUMNS_ALERT = {
  type: 'info' as TNotificationAlert,
  description:
    'Match each expected column to the corresponding one from your CSV file.\nThis ensures we import your address data correctly.',
};

export const REPAIR_ADDRESSES_ALERT = {
  type: 'info' as TNotificationAlert,
  description:
    'Our system checks the values of each row and if any issues are found you can easily edit it by clicking on the cell',
};

export const REPAIR_ADDRESSES_SUCCESS_ALERT = {
  type: 'success' as TNotificationAlert,
  title: 'No issues remain',
  description: 'Click Return to full view to see all rows, or Next to start the import',
  buttonText: 'Return to full view',
};

export const REPAIR_ADDRESSES_NO_ROWS_FOUND_ALERT = {
  type: 'info' as TNotificationAlert,
  title: 'No rows found',
  description: "You've removed all addresses. Upload a new file to continue.",
  buttonText: 'Back to upload',
};

export const REPAIR_ADDRESSES_NO_RESULTS_ALERT = {
  type: 'info' as TNotificationAlert,
  title: (searchQuery: string) => `No results for "${searchQuery}"`,
  description: 'Try a different search term',
};

export const IMPORT_ADDRESSES_SUCCESS_ALERT = {
  type: 'success' as TNotificationAlert,
  title: (addedAddressesLabel: string) => `${addedAddressesLabel} uploaded successfully`,
  description: 'Your CSV file has been processed.',
  amountOfAddresses: (addedAddressesLabel: string) => `${addedAddressesLabel} added to your address book.`,
  buttonText: 'Go to address book',
};

export const COLUMN_ISSUES_TOAST = {
  type: 'error' as TNotificationAlert,
  description: (amountOfIssues: number) =>
    `${getCountLabel(amountOfIssues, 'column issue', 'column issues')} found — fix before continuing`,
};

export const INVALID_ROWS_TOAST = {
  type: 'error' as TNotificationAlert,
  description: (invalidRowsCount: number) =>
    `${getCountLabel(invalidRowsCount, 'row contains error', 'rows contain errors')}.`,
  buttonText: 'View errors',
};

export const REPAIR_ADDRESSES_SUCCESS_TOAST = {
  type: 'success' as TNotificationAlert,
  description: 'All good! You are ready to import data',
};

export const ADDED_ADDRESSES_EXPIRATION_TIME_MS = 5000;
